import { CustomForm } from '../hooks/useCustomForm'
import { Waiver } from '../hooks/useWaivers'

export type User = {
  id: string
  name: string
  imageId: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  zip: string
  country: string
  createdAt: string
  updatedAt: string
}

export type PendingDeposit = {
  id: string
  status: string | null
  created_at: string
  user: User
  root_schedule_id: string
  root_schedule_name: string
  schedule_id: string
  schedule_name: string
  team_id: string
  team_name: string
  team_image: string | null
  deposit_amount: number
  currency: number
  due_date: string
}

export type PendingInstallment = {
  id: string
  status: string | null
  created_at: string
  user: User
  root_schedule_id: string
  root_schedule_name: string
  schedule_id: string
  schedule_name: string
  team_id: string
  team_name: string
  team_image: string | null
  payment_amount: number
  currency: number
  due_date: string
}

export interface PlayerConflictOptions {
  uid: string
  sport_id: number | null
  name_full: string
  created_at: string
  updated_at: string
  team_details?: string
}

export interface PlayerConflict {
  daysmart_id: number
  name_full: string
  email: string
  birth_date: string | null
  player_conflicts: PlayerConflictOptions[]
}

export interface TeamConflictOptions {
  uid: string
  name: string
  created_at: string
  updated_at: string
}

export interface TeamConflict {
  daysmart_id: number
  name: string
  team_conflicts: TeamConflictOptions[]
}

export interface FacilityConflictOptions {
  uid: string
  name: string
  created_at: string
  updated_at: string
}

export interface FacilityConflict {
  daysmart_id: number
  name: string
  facility_conflicts: FacilityConflictOptions[]
}

export interface TeamOfficialConflictOptions {
  uid: string
  name: string
  name_full: string
  created_at: string
  updated_at: string
}

export interface TeamOfficialConflict {
  daysmart_id: number
  name: string
  name_full: string
  team_official_conflicts: TeamOfficialConflictOptions[]
}

export interface Conflicts {
  count: number
  players: {
    count: number
    data: PlayerConflict[]
  }
  team_officials: {
    count: number
    data: TeamOfficialConflict[]
  }
  teams: {
    count: number
    data: TeamConflict[]
  }
  facilities: {
    count: number
    data: FacilityConflict[]
  }
}

export interface Success {
  count: number
  data: any[]
}

export interface Failures {
  count: number
  data: any[]
}

export interface DaysmartResponse {
  success: Success
  conflicts: Conflicts
  failures: Failures
}

export type Currency = {
  id: string
  name: string
  name_full: string | null
  abbreviation: string | null
}

export type Division = {
  id: string
  name: string
  description?: string | null
  schedule_id: string
  is_team_registration: boolean
  is_player_registration: boolean
  auto_approve: boolean
  is_registration_on: number
  organization_id: string
  organization_name: string
  official_governing_body_event_number: string | null
  currency: Currency
  day_of_week: {
    id: string
    name: string
    name_full: string | null
    abbreviation: string | null
  }
  division_gender: {
    id: string
    name: string
    name_full: string | null
    abbreviation: string | null
  }
  max_teams: number
  team_count_enrolled: number
  team_count: number
  teams: Array<{
    id: string
    name: string
    image: string | null
  }>
  max_players_per_team: number
  max_team_rep_per_team: number
  division_age: {
    id: string
    name: string
    name_full: string | null
    abbreviation: string | null
  }
  deposit: number
  deposit_required: boolean
  single_payment: boolean
  team_cost: number
  installments: number
  installment_start_date: string
  installment_frequency: string | null
  player_deposit_required: boolean
  player_deposit: number | null
  player_single_payment: number
  player_cost: string
  player_installments: number | null
  player_installment_start_date: string
  player_installment_frequency: string | null
  show_existing_registrations_teams: boolean
  show_existing_registrations_players: boolean
  division_age_v2: string | null
  division_day_of_week_v2: string | null
}

export type RootSchedule = {
  id: string
  name: string
  name_full: string
  type: string
  abbreviation: string
  email: string | null
  website: string | null
  phone: string | null
  starts_at: string
  can_create_referees: boolean
  can_create_scorekeepers: boolean
  ends_at: string
  is_tournament: boolean
  tournament_status: number
  tournament_can_sync_regular_season: boolean
  owned_by_account_id: string
  is_public: boolean
  is_registration_on: boolean
  sport_id: string
  generic_sport_id: string | null
  generic_sport_name: string | null
  organization_id: string
  parent_id: string | null
  image: string | null
  settings: any[]
  deposit: number | null
  online_registration_open: boolean
  season_currency: string | null
  season_type: string | null
  tax_rate: string | null
  division_group: string | null
  division_age: string | null
  division_gender: string | null
  team_cost: number | null
  sort_order: number
  day_of_week: string | null
  is_active: boolean
  salesforce_id: string | null
  is_favorite: boolean
  games_count: number
  teams_count: number
  registration_version: number
}

export type AvailableTax = {
  id: string
  name: string
  description: string
  tax_rate: string
  currency: Currency
}

export type Registration = {
  id: string
  name: string
  schedule_id: string
  root_schedule_id: string
  root_schedule: RootSchedule
  is_registration_on: boolean
  organization_id: string
  sport_id: string
  organization_name: string
  official_governing_body_event_number: string
  currency: Currency
  standard_cost: number
  payment_currency: Currency
  payment_type: {
    id: string
    name: string
    name_full: string | null
    abbreviation: string | null
  }
  is_live: boolean
  image?: Image
  starts_at: string
  ends_at: string
  custom_link: string
  dark_theme: boolean
  push_notification: boolean
  player_registration_allowed: boolean
  bcc_registrations_to: string
  available_taxes: AvailableTax[]
  taxes: AvailableTax[]
  location?: string | null
  registration_type_id: number
  is_team_registration: boolean
  is_player_registration: boolean
  auto_approve: boolean
  show_waiver: boolean
  waivers: Waiver[]
  can_add_players_from_roster: boolean
  payment_provider_type: {
    id: string
    name: string
  }
  player_form: CustomForm | null
  team_form: CustomForm | null
  header_content: string | null
  team_confirmation_email_content?: string | null
  player_confirmation_email_content?: string | null
  defer_payment: boolean
}

export type Event = {
  id: string
  name: string
  abbreviation: string
  image?: Image
  type: string
  sport_id: string
  registration: Registration
  divisions: Division[]
  location: string
}

type ImageFormat = {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
}

type Image = {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
  formats: ImageFormat[]
}

export enum RegistrationSteps {
  CREATED = 'created',
  SELECT_YOUR_DIVISION = 'select_division',
  SELECT_YOUR_ROLE = 'registration_type',
  SIGN_UP = 'sign_up',
  TEAM_REGISTER = 'team_register',
  REGISTRATION_PAYMENT = 'payment',
  VALIDATE_EMAIL = 'validate_email',
  CONFIRM = 'confirm',
  WAIVER = 'waiver',
}

export interface GameTiming {
  default_period_length: number
  default_overtime_length: number
  regulation_game_length: number
}

export interface GameSignatures {
  skip_official: boolean
  skip_team_official: boolean
}

export interface GameScoring {
  require_time_game_event: boolean
  require_goal_type: boolean
  require_goal_configuration: boolean
  scoring_values: number[]
  assign_points_to_player?: boolean
  allow_team_official?: boolean
}
export interface GameSettings {
  signatures: GameSignatures
  timing: GameTiming
  scoring: GameScoring
  skip_lineup: boolean
  require_officials: boolean
  require_unique_jersey_numbers: boolean
  require_waiver_signature: boolean
  require_goalie: boolean
  rosters?: {
    require: boolean
  }
}

export interface StatsPoints {
  ties: number
  wins: number
  losses: number
  overtime_loss: number
  overtime_win: number
  shootout_loss: number
}

export interface StatsGoalsProps {
  forfeit: number
}

export interface StatsGoals {
  per: StatsGoalsProps
}

export interface StatsSettings {
  goals: StatsGoals
  points: StatsPoints
  player?: {
    displayed?: number[]
  }
  goalie?: {
    displayed?: number[]
  }
}

export interface StandingsSettings {
  displayed?: number[]
  sort_order: {
    stat_id: number | null
    tie_breaker_stats: number[]
  }
}

export interface Settings {
  game: GameSettings
  stats: StatsSettings
  standings: StandingsSettings
}

export interface StatType {
  id: number
  name: string
  abbreviation: string
  description: string
  sport_id?: string
  is_player_stat: boolean
  is_team_stat: boolean
  is_sort_desc: boolean
  is_h2h: boolean
}

export interface SportSettings {
  id: string
  name: string
  settings: Settings
}

export interface VersionInfo {
  minimum_version: string
  suggested_version: string
}

export interface ApiInfo {
  current_version: string
}

export interface VersionConfig {
  web: VersionInfo
  mobile: VersionInfo
  api: ApiInfo
}

// API Request Hook Types
export * from './api'
